import { useState } from 'react';
import {
  X,
  Send,
  Building2,
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  Printer,
  Download,
  FileText,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import { Button } from '@/components/ui/button';
import type { PurchaseRequest } from '../../types';
import {
  PR_STATUS_LABELS,
  PR_STATUS_COLORS,
  PR_PRIORITY_LABELS,
  PR_PRIORITY_COLORS,
} from '../../types';
import type {
  TabValue,
  Tab,
} from './types';
import { DetailsTab } from './DetailsTab';
import { ItemsTab } from './ItemsTab';
import { TimelineTab } from './TimelineTab';

interface PurchaseRequestDetailModalProps {
  readonly request: PurchaseRequest;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onApprove?: (requestId: string) => void;
  readonly onReject?: (requestId: string) => void;
  readonly onCreateSourcingEvent?: (requestId: string) => void;
}

const TABS: Tab[] = [
  {
    value: 'details',
    label: 'Details',
    icon: <FileText className="h-4 w-4" />,
  },
  {
    value: 'items',
    label: 'Line Items',
    icon: <Package className="h-4 w-4" />,
  },
  {
    value: 'timeline',
    label: 'Timeline',
    icon: <Clock className="h-4 w-4" />,
  },
];

export function PurchaseRequestDetailModal({
  request,
  isOpen,
  onClose,
  onApprove,
  onReject,
  onCreateSourcingEvent,
}: PurchaseRequestDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabValue>('details');

  if (!isOpen) return null;

  const isPending = request.status === 'pending_approval';
  const isApproved = request.status === 'approved';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-50 flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
        <ModalHeader request={request} onClose={onClose} />
        <TabNavigation tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'details' && <DetailsTab request={request} />}
          {activeTab === 'items' && <ItemsTab request={request} />}
          {activeTab === 'timeline' && <TimelineTab request={request} />}
        </div>
        <ModalFooter
          request={request}
          isPending={isPending}
          isApproved={isApproved}
          onApprove={onApprove}
          onReject={onReject}
          onCreateSourcingEvent={onCreateSourcingEvent}
        />
      </div>
    </div>
  );
}

function ModalHeader({
  request,
  onClose,
}: {
  readonly request: PurchaseRequest;
  readonly onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b px-6 py-4">
      <div className="flex items-center gap-4">
        <div className="rounded-lg bg-amber-100 p-3">
          <Send className="h-6 w-6 text-amber-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{request.title}</h2>
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Building2 className="h-4 w-4" />
            {request.requestNumber}
          </p>
        </div>
        <span
          className={cn(
            'ml-2 rounded-full px-3 py-1 text-sm font-medium',
            PR_PRIORITY_COLORS[request.priority]
          )}
        >
          {PR_PRIORITY_LABELS[request.priority]}
        </span>
        <span
          className={cn('rounded-full px-3 py-1 text-sm font-medium', PR_STATUS_COLORS[request.status])}
        >
          {PR_STATUS_LABELS[request.status]}
        </span>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

function TabNavigation({
  tabs,
  activeTab,
  onTabChange,
}: {
  readonly tabs: Tab[];
  readonly activeTab: TabValue;
  readonly onTabChange: (tab: TabValue) => void;
}) {
  return (
    <div className="flex gap-1 border-b px-6">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onTabChange(tab.value)}
          className={cn(
            'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
            activeTab === tab.value
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
          )}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

interface ModalFooterProps {
  readonly request: PurchaseRequest;
  readonly isPending: boolean;
  readonly isApproved: boolean;
  readonly onApprove?: (requestId: string) => void;
  readonly onReject?: (requestId: string) => void;
  readonly onCreateSourcingEvent?: (requestId: string) => void;
}

function ModalFooter({
  request,
  isPending,
  isApproved,
  onApprove,
  onReject,
  onCreateSourcingEvent,
}: ModalFooterProps) {
  return (
    <div className="flex items-center justify-between border-t px-6 py-4">
      <div className="flex gap-2">
        <Button variant="outline" size="sm">
          <Printer className="mr-2 h-4 w-4" />
          Print
        </Button>
        <Button variant="outline" size="sm">
          <Download className="mr-2 h-4 w-4" />
          Export PDF
        </Button>
      </div>
      <div className="flex gap-2">
        {isPending && onReject && (
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700"
            onClick={() => onReject(request.requestId)}
          >
            <XCircle className="mr-2 h-4 w-4" />
            Reject
          </Button>
        )}
        {isPending && onApprove && (
          <Button size="sm" onClick={() => onApprove(request.requestId)}>
            <CheckCircle2 className="mr-2 h-4 w-4" />
            Approve
          </Button>
        )}
        {isApproved && onCreateSourcingEvent && (
          <Button size="sm" onClick={() => onCreateSourcingEvent(request.requestId)}>
            <Send className="mr-2 h-4 w-4" />
            Create Sourcing Event
          </Button>
        )}
      </div>
    </div>
  );
}
